import React, { useCallback, useEffect, useMemo } from "react";
import cn from "classnames";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import useTooltipStyles from "./style";
import CustomColorPicker from "./CustomColorPicker";
import { getOpacityPercentage, hideSelectionTemporarily, setOpacityPercentage } from "../../utils";
import { Title } from "./NewEffectSidebar";
import { useColorPickerContext } from "./ColorPickerContext";
import { useDraggableColorPicker } from "./useDraggableColorPicker";

/**
 * Helper function to update color in shape props
 * For mask groups with colorMix, updates ALL colorMix values
 */
function updateColorInProps(updatedProps: any, colorProp: string, newColor: string): void {
  // For mask groups with colorMix, update all colorMix values
  if (updatedProps.colorMix && typeof updatedProps.colorMix === "object") {
    updatedProps.colorMix = { ...updatedProps.colorMix };
    Object.keys(updatedProps.colorMix).forEach((key) => {
      if (updatedProps.colorMix[key] && typeof updatedProps.colorMix[key] === "object") {
        updatedProps.colorMix[key] = {
          ...updatedProps.colorMix[key],
          color: newColor,
        };
      }
    });
  }
  // Also update the colorProp directly (fill, color, etc.)
  updatedProps[colorProp] = newColor;
}

// Baseline anchor offset applied to the popover Paper. The picker is rendered
// to the LEFT of its trigger by translating -245px on X (preserved from the
// original implementation). The `var()` slots let `useDraggableColorPicker`
// layer a drag offset on top without losing the baseline by writing the two
// CSS variables directly to the Paper element. `!important` keeps MUI's own
// animation transforms from fighting our offset.
const useStyles = makeStyles(() => ({
  paper: {
    transform: "translate(calc(-245px + var(--cp-drag-x, 0px)), var(--cp-drag-y, 0px)) !important",
  },
}));

interface ColorPickerPopoverProps {
  colorProp: string;
  selectedShape: any;
  editor: any;
  popoverStyle?: boolean;
  fullWidth?: boolean;
  setSelectedShape: any;
  color: string;
  selectedShapes: any[];
}

const ColorPickerPopover = ({
  colorProp,
  selectedShape,
  editor,
  popoverStyle,
  fullWidth,
  setSelectedShape,
  color,
  selectedShapes,
}: ColorPickerPopoverProps) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentColor, setCurrentColor] = React.useState<string>(color);
  const [opacity, setOpacity] = React.useState<string>("");
  const [isEditingOpacity, setIsEditingOpacity] = React.useState(false);
  const [opacityInput, setOpacityInput] = React.useState("");
  const opacityInputRef = React.useRef<HTMLInputElement | null>(null);
  const [opacityDragPending, setOpacityDragPending] = React.useState(false);
  const [opacityDragging, setOpacityDragging] = React.useState(false);
  const opacityDragStart = React.useRef({ x: 0, val: 0 });
  // Separate editing value that's completely independent of currentColor during typing
  const [editingHexValue, setEditingHexValue] = React.useState<string | null>(null);
  // Stores the color we just applied — useEffect skips stale-prop updates until props catch up
  const justAppliedColorRef = React.useRef<string | null>(null);

  const classes = useStyles();
  const classesTooltip = useTooltipStyles();

  // Use color picker context for global state management
  const { openColorPicker, closeColorPicker, isOpen } = useColorPickerContext();
  const pickerId = useMemo(() => `color-picker-${colorProp}-${selectedShape?.id}`, [colorProp, selectedShape?.id]);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
    openColorPicker(pickerId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    closeColorPicker(pickerId);
  };

  // Close this picker if another one opens
  React.useEffect(() => {
    if (!isOpen(pickerId) && anchorEl) {
      setAnchorEl(null);
    }
  }, [isOpen, pickerId, anchorEl]);

  const open = Boolean(anchorEl) && isOpen(pickerId);

  // Drag + outside-click behaviour is shared with the brand-kit and
  // Settings color pickers via `useDraggableColorPicker`. The hook
  // exposes the Paper content ref + a `handleBorderMouseDown` that's
  // wired to the wrapper div — clicks on the free border start a
  // drag, clicks on descendants pass through to swatches/inputs.
  const { paperContentRef, handleBorderMouseDown, isDragging } = useDraggableColorPicker(
    open,
    anchorEl as Element | null,
    handleClose
  );

  const updateShapes = (updateFn: (shape: any) => any) => {
    editor.run(
      () => {
        selectedShapes.forEach((shape) => {
          const updatedShape = updateFn(shape);
          editor.updateShape(updatedShape);
          // Only update selectedShape if this is the currently selected shape
          if (selectedShape && selectedShape.id === shape.id) {
            setSelectedShape(updatedShape);
          }
        });
      },
      { ignoreShapeLock: true }
    );
  };

  const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only update the isolated editing value while typing, apply on blur
    setEditingHexValue(e.target.value.trim());
  };

  const applyColorToShapes = (newColor: string) => {
    const newOpacity = getOpacityPercentage(newColor);
    setOpacity(`${newOpacity}`);

    updateShapes((shape) => {
      const updatedProps = { ...shape.props };

      if (colorProp.startsWith("outlineColorMix.")) {
        const key = colorProp.split(".")[1];
        updatedProps.outlineColorMix = { ...updatedProps.outlineColorMix };
        if (updatedProps.outlineColorMix[key]) {
          updatedProps.outlineColorMix[key] = {
            color: newColor,
            isHidden: updatedProps.outlineColorMix[key].isHidden,
          };
        } else {
          updatedProps.outlineColorMix[key] = { color: newColor, isHidden: false };
        }
      } else if (colorProp.startsWith("colorMix.")) {
        const key = colorProp.split(".")[1];
        updatedProps.colorMix = { ...updatedProps.colorMix };
        if (updatedProps.colorMix[key]) {
          updatedProps.colorMix[key] = { color: newColor, isHidden: updatedProps.colorMix[key].isHidden };
        } else {
          updatedProps.colorMix[key] = { color: newColor, isHidden: false };
        }
      } else if (colorProp.startsWith("outShadowArray.") || colorProp.startsWith("inShadowArray.")) {
        const [_, shadowId, prop] = colorProp.split(".");
        const shadowType = colorProp.startsWith("outShadowArray.") ? "outShadowArray" : "inShadowArray";
        if (shadowId && prop) {
          updatedProps[shadowType] = {
            ...updatedProps[shadowType],
            [shadowId]: {
              ...updatedProps[shadowType][shadowId],
              [prop]: newColor,
            },
          };
        }
      } else {
        updateColorInProps(updatedProps, colorProp, newColor);
      }

      return {
        ...shape,
        props: updatedProps,
      };
    });

    // Only call hideSelectionTemporarily if selectedShape is not a group
    if (selectedShape && selectedShape.type !== "group") {
      hideSelectionTemporarily(selectedShape, editor);
    }
  };

  // Direct shape update matching CustomColorPicker's updateShapeProps pattern
  const directUpdateShape = (shape: any, newColor: string) => {
    const updatedProps = { ...shape.props };
    if (colorProp.startsWith("colorMix.")) {
      const index = colorProp.split(".")[1];
      updatedProps.colorMix = { ...updatedProps.colorMix };
      updatedProps.colorMix[index] = { color: newColor, isHidden: updatedProps.colorMix[index]?.isHidden ?? false };
    } else if (colorProp.startsWith("outlineColorMix.")) {
      const index = colorProp.split(".")[1];
      updatedProps.outlineColorMix = { ...updatedProps.outlineColorMix };
      updatedProps.outlineColorMix[index] = {
        color: newColor,
        isHidden: updatedProps.outlineColorMix[index]?.isHidden ?? false,
      };
    } else if (colorProp.startsWith("outShadowArray.") || colorProp.startsWith("inShadowArray.")) {
      const [_, shadowId, prop] = colorProp.split(".");
      const shadowType = colorProp.startsWith("outShadowArray.") ? "outShadowArray" : "inShadowArray";
      if (shadowId && prop) {
        updatedProps[shadowType] = {
          ...updatedProps[shadowType],
          [shadowId]: {
            ...updatedProps[shadowType][shadowId],
            id: shadowId,
            [prop]: newColor,
            dx: updatedProps[shadowType][shadowId]?.dx ?? 0,
            dy: updatedProps[shadowType][shadowId]?.dy ?? 0,
            shadowBlur: updatedProps[shadowType][shadowId]?.shadowBlur ?? 0,
            opacity: updatedProps[shadowType][shadowId]?.opacity ?? 1,
            isHidden: updatedProps[shadowType][shadowId]?.isHidden ?? false,
          },
        };
      }
    } else if (colorProp) {
      updateColorInProps(updatedProps, colorProp, newColor);
    }
    const updatedShape = { ...shape, props: updatedProps };
    editor.updateShape(updatedShape);
    if (selectedShape && selectedShape.id === shape.id) {
      setSelectedShape(updatedShape);
    }
    return updatedShape;
  };

  const handleColorInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const typedValue = e.target.value.trim();
    setEditingHexValue(null);

    const isLikelyHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(typedValue);
    if (isLikelyHex) {
      justAppliedColorRef.current = typedValue.toLowerCase();
      setCurrentColor(typedValue);
      setOpacity(`${getOpacityPercentage(typedValue)}`);

      // Direct editor update — same pattern as CustomColorPicker's updateShapeProps
      editor.run(
        () => {
          selectedShapes.forEach((shape) => {
            directUpdateShape(shape, typedValue);
          });
        },
        { ignoreShapeLock: true }
      );

      if (selectedShape && selectedShape.type !== "group") {
        hideSelectionTemporarily(selectedShape, editor);
      }

      setTimeout(() => {
        justAppliedColorRef.current = null;
      }, 1000);
    }
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only call hideSelectionTemporarily if selectedShape is not a group
    if (selectedShape && selectedShape.type !== "group") {
      hideSelectionTemporarily(selectedShape, editor);
    }

    const newOpacity = e.target.value.replace("%", "").trim();
    if (
      newOpacity === "" ||
      (!isNaN(parseInt(newOpacity, 10)) && parseInt(newOpacity, 10) >= 0 && parseInt(newOpacity, 10) <= 100)
    ) {
      setOpacity(newOpacity === "" ? "0" : `${parseInt(newOpacity, 10)}`);
      if (newOpacity !== "") {
        const opacityValue = parseInt(newOpacity, 10);
        const newColor = setOpacityPercentage(currentColor as string, opacityValue);
        setCurrentColor(newColor);

        updateShapes((shape) => {
          const updatedProps = { ...shape.props };

          if (colorProp.startsWith("outlineColorMix.")) {
            const key = colorProp.split(".")[1];
            updatedProps.outlineColorMix = { ...updatedProps.outlineColorMix };
            if (updatedProps.outlineColorMix[key]) {
              updatedProps.outlineColorMix[key] = {
                color: newColor,
                isHidden: updatedProps.outlineColorMix[key].isHidden,
              };
            } else {
              updatedProps.outlineColorMix[key] = { color: newColor, isHidden: false };
            }
          } else if (colorProp.startsWith("colorMix.")) {
            const key = colorProp.split(".")[1];
            updatedProps.colorMix = { ...updatedProps.colorMix };
            if (updatedProps.colorMix[key]) {
              updatedProps.colorMix[key] = { color: newColor, isHidden: updatedProps.colorMix[key].isHidden };
            } else {
              updatedProps.colorMix[key] = { color: newColor, isHidden: false };
            }
          } else if (colorProp.startsWith("outShadowArray.") || colorProp.startsWith("inShadowArray.")) {
            const [_, shadowId, prop] = colorProp.split(".");
            const shadowType = colorProp.startsWith("outShadowArray.") ? "outShadowArray" : "inShadowArray";
            if (shadowId && prop) {
              updatedProps[shadowType] = {
                ...updatedProps[shadowType],
                [shadowId]: {
                  ...updatedProps[shadowType][shadowId],
                  [prop]: newColor,
                },
              };
            }
          } else {
            updateColorInProps(updatedProps, colorProp, newColor);
          }

          return {
            ...shape,
            props: updatedProps,
          };
        });

        // Only call hideSelectionTemporarily if selectedShape is not a group
        if (selectedShape && selectedShape.type !== "group") {
          hideSelectionTemporarily(selectedShape, editor);
        }
      }
    }
  };

  const handleColorChange = (newColor: string) => {
    setCurrentColor(newColor);
    applyColorToShapes(newColor);
  };

  React.useEffect(() => {
    if (!opacityDragPending) return;
    let isDrag = false;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - opacityDragStart.current.x;
      if (!isDrag) {
        if (Math.abs(delta) < 3) return;
        isDrag = true;
        setOpacityDragging(true);
        e.preventDefault();
      }
      const next = Math.max(0, Math.min(100, Math.round(opacityDragStart.current.val + delta)));
      handleOpacityChange({ target: { value: String(next) } } as any);
    };
    const onUp = () => {
      const wasDrag = isDrag;
      setOpacityDragPending(false);
      setOpacityDragging(false);
      if (!wasDrag) {
        // Click without drag → enter edit mode
        setOpacityInput(opacity);
        setIsEditingOpacity(true);
        setTimeout(() => {
          opacityInputRef.current?.focus();
          opacityInputRef.current?.select();
        }, 0);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [opacityDragPending, opacity]);

  useEffect(() => {
    // Compute the color that props currently reflect
    let newColor = "#e3e3e3"; // Default fallback

    if (colorProp.startsWith("colorMix.")) {
      const index = parseInt(colorProp.split(".")[1], 10);
      if (selectedShape?.props?.colorMix && selectedShape.props.colorMix[index]) {
        newColor = selectedShape.props.colorMix[index].color || newColor;
      }
    } else if (colorProp.startsWith("outlineColorMix.")) {
      const index = parseInt(colorProp.split(".")[1], 10);
      if (selectedShape?.props?.outlineColorMix && selectedShape.props.outlineColorMix[index]) {
        newColor = selectedShape.props.outlineColorMix[index].color || newColor;
      }
    } else if (colorProp.startsWith("outShadowArray.") || colorProp.startsWith("inShadowArray.")) {
      const [_, shadowId, prop] = colorProp.split(".");
      const shadowType = colorProp.startsWith("outShadowArray.") ? "outShadowArray" : "inShadowArray";
      if (shadowId && prop && selectedShape?.props?.[shadowType]?.[shadowId]) {
        const shadow = selectedShape.props[shadowType][shadowId];
        if (shadow && shadow[prop]) {
          newColor = shadow[prop];
        }
      }
    } else {
      const propColor = selectedShape?.props?.[colorProp];
      newColor = propColor || color || newColor;
    }

    // If we just applied a color, skip until props reflect the applied value
    if (justAppliedColorRef.current) {
      if (newColor.toLowerCase() === justAppliedColorRef.current.toLowerCase()) {
        // Props have caught up — clear the flag, currentColor is already correct
        justAppliedColorRef.current = null;
      }
      // Either way, don't overwrite currentColor while waiting for props to sync
      return;
    }

    setCurrentColor(newColor);
    setOpacity(`${getOpacityPercentage(newColor)}`);
  }, [selectedShape, colorProp, color]);

  return (
    <div className={fullWidth ? "w-full" : undefined}>
      <div className={cn("flex items-center justify-between", fullWidth ? "w-full" : "w-[150px]")}>
        <Tooltip
          title={"Select the color of the shape"}
          arrow
          classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
        >
          <div
            className={cn(
              popoverStyle ? "flex items-center" : "flex items-center w-full py-1 px-2 rounded-[4px] relative"
            )}
            style={popoverStyle ? {} : { border: "1px solid #E2E2FC" }}
          >
            {popoverStyle && <p className="text-[#707087] !text-[9px] leading-[10px]">Color</p>}
            <button
              style={{
                backgroundColor: currentColor,
              }}
              className={cn(
                popoverStyle
                  ? "min-w-[10px] h-[10px] rounded-[1.5px] ml-1 mr-[3px] color-picker-border"
                  : "min-w-4 min-h-4 rounded-[4px] mr-[6px] color-picker-border"
              )}
              aria-describedby={`${colorProp}-${selectedShape.id}`}
              onClick={handleClick}
            />
            <input
              type="text"
              value={(editingHexValue !== null ? editingHexValue : currentColor).toUpperCase()}
              onChange={handleColorInput}
              onFocus={() => {
                setEditingHexValue(currentColor);
              }}
              onBlur={handleColorInputBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className={cn(
                popoverStyle
                  ? "h-[10px] max-w-[37px] text-[7px] rounded-[1.5px] w-[55px] px-1 color-picker-border mr-1"
                  : // Default (non-popoverStyle) variant: bump width
                    // from 55px → 72px so 8-digit hex codes like
                    // "#FF0000FF" (RGB + alpha = 9 chars) don't get
                    // clipped. The 10px font + px-1 padding needs ~70px
                    // for the full string at default letter spacing.
                    // `flex-shrink-0` keeps the input from squashing
                    // inside the parent flex layout (shadow popover,
                    // shape sidebar) when other siblings want space.
                    "h-6 text-[10px] rounded-[4px] w-[72px] flex-shrink-0 px-1 mr-1",
                "flex items-center justify-center text-left"
              )}
            />

            {!popoverStyle && (
              <>
                {/* Separator at 3/4 from left */}
                <div
                  className="absolute h-4 bg-[#E2E2FC]"
                  style={{
                    left: "70%",
                    width: "1px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />

                {/* Opacity percentage near right edge - editable */}
                {isEditingOpacity ? (
                  <input
                    ref={opacityInputRef}
                    type="text"
                    value={opacityInput}
                    onChange={(e) => {
                      const value = e.target.value.replace("%", "").trim();
                      if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100)) {
                        setOpacityInput(value);
                      }
                    }}
                    onBlur={() => {
                      const opacityValue = opacityInput === "" ? 100 : Math.min(100, Math.max(0, Number(opacityInput)));
                      const newColor = setOpacityPercentage(currentColor as string, opacityValue);
                      setCurrentColor(newColor);
                      setOpacity(`${opacityValue}`);

                      updateShapes((shape) => {
                        const updatedProps = { ...shape.props };

                        if (colorProp.startsWith("outlineColorMix.")) {
                          const key = colorProp.split(".")[1];
                          updatedProps.outlineColorMix = { ...updatedProps.outlineColorMix };
                          if (updatedProps.outlineColorMix[key]) {
                            updatedProps.outlineColorMix[key] = {
                              color: newColor,
                              isHidden: updatedProps.outlineColorMix[key].isHidden,
                            };
                          } else {
                            updatedProps.outlineColorMix[key] = { color: newColor, isHidden: false };
                          }
                        } else if (colorProp.startsWith("colorMix.")) {
                          const key = colorProp.split(".")[1];
                          updatedProps.colorMix = { ...updatedProps.colorMix };
                          if (updatedProps.colorMix[key]) {
                            updatedProps.colorMix[key] = {
                              color: newColor,
                              isHidden: updatedProps.colorMix[key].isHidden,
                            };
                          } else {
                            updatedProps.colorMix[key] = { color: newColor, isHidden: false };
                          }
                        } else if (colorProp.startsWith("outShadowArray.") || colorProp.startsWith("inShadowArray.")) {
                          const [_, shadowId, prop] = colorProp.split(".");
                          const shadowType = colorProp.startsWith("outShadowArray.")
                            ? "outShadowArray"
                            : "inShadowArray";
                          if (shadowId && prop) {
                            updatedProps[shadowType] = {
                              ...updatedProps[shadowType],
                              [shadowId]: {
                                ...updatedProps[shadowType][shadowId],
                                [prop]: newColor,
                                dx: updatedProps[shadowType][shadowId].dx,
                                dy: updatedProps[shadowType][shadowId].dy,
                              },
                            };
                          }
                        } else {
                          updateColorInProps(updatedProps, colorProp, newColor);
                        }

                        return {
                          ...shape,
                          props: updatedProps,
                        };
                      });

                      setIsEditingOpacity(false);
                      setOpacityInput("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      } else if (e.key === "Escape") {
                        setIsEditingOpacity(false);
                        setOpacityInput("");
                      }
                    }}
                    className="ml-auto text-[10px] text-[#2E3860] pr-1 w-10 text-right bg-transparent border-none outline-none focus:outline-none"
                    autoFocus
                  />
                ) : (
                  <span
                    className="ml-auto text-[10px] text-[#2E3860] pr-1 hover:opacity-70 select-none"
                    style={{ cursor: opacityDragging ? "ew-resize" : "ew-resize" }}
                    title="Drag to adjust opacity, click to type"
                    onMouseDown={(e) => {
                      opacityDragStart.current = {
                        x: e.clientX,
                        val: parseInt(opacity || "0", 10) || 0,
                      };
                      setOpacityDragPending(true);
                      setOpacityDragging(false);
                    }}
                  >
                    {opacity}%
                  </span>
                )}
              </>
            )}

            {popoverStyle && (
              <div className="relative ml-auto">
                <input
                  type="text"
                  value={`${opacity}`}
                  className="ml-auto w-[25px] h-[10px] text-[7px] pr-[8px] rounded-[1.5px] text-end flex items-center justify-center color-picker-border"
                  onChange={handleOpacityChange}
                />
                <span className="top-[0px] right-[3px] text-[7px] absolute text-end">%</span>
              </div>
            )}
          </div>
        </Tooltip>

        <Popover
          id={`${colorProp}-${selectedShape.id}`}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          classes={{ paper: classes.paper }}
          style={{ pointerEvents: "none" }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          keepMounted
          BackdropProps={{ invisible: true, style: { pointerEvents: "none" } }}
          // Transform itself is set on `classes.paper` (with !important) so
          // MUI's animation transforms don't clobber it. The drag offset
          // is layered on top via the `--cp-drag-x` / `--cp-drag-y` CSS
          // variables that `classes.paper` reads — managed by the hook
          // directly on the Paper element (no inline style here so React
          // re-renders never clobber the in-flight drag value).
          // `borderRadius` + `pointerEvents: auto` stay here.
          PaperProps={{ style: { pointerEvents: "auto", borderRadius: "8px" } }}
        >
          {/* The picker's "free border" (the p-4 padding around
              CustomColorPicker) doubles as the drag handle: clicks on
              the padded frame fire handleBorderMouseDown (because the
              click target is the wrapper itself), clicks on the
              picker content bubble up but get ignored by the target
              check. Replaces the previous 6-dot top-right handle. */}
          <div
            ref={paperContentRef}
            onMouseDown={handleBorderMouseDown}
            className="p-4 rounded-[8px] w-[264px] h-[442px] overflow-y-auto"
            // `alignContent: center` (CSS Box Alignment L3 on a plain
            // block) vertically centers the inner picker so the empty
            // space above and below it matches. Paired with
            // `alignSelf: center` on the picker root in CustomColor-
            // Picker.tsx.
            style={{ cursor: isDragging ? "grabbing" : "grab", alignContent: "center" }}
          >
            <CustomColorPicker
              editor={editor}
              colorProp={colorProp}
              shapeColor={currentColor}
              onColorChange={handleColorChange}
              selectedShape={selectedShape}
            />
          </div>
        </Popover>
      </div>
    </div>
  );
};

export default ColorPickerPopover;

// Custom CSS for color picker borders
const colorPickerBorderStyle = `
  .color-picker-border {
    border: 1px solid #E1E6EB !important;
    box-sizing: border-box !important;
  }
`;

// Inject the style into the document head
if (typeof document !== "undefined") {
  const styleId = "color-picker-border-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = colorPickerBorderStyle;
    document.head.appendChild(style);
  }
}
